import { s3baseUrl } from "../config/config";
import i18n from "../i18n";

/** Structural entries for built-in lesson books (titles come from i18n via buildDefaultBooks). */
export const DEFAULT_BOOK_ENTRIES = [
  {
    _id: "default-quran",
    slug: "quran-pak",
    path: "/lesson-books/quran-pak",
    iconClass: "lesson-book-card-icon-quran",
    icon: "fas fa-book-open",
    titleKey: "lessonBooks.quran.cardTitle",
    descriptionKey: "lessonBooks.quran.cardDescription",
  },
  {
    _id: "default-quida",
    slug: "quida-english",
    path: "/lesson-books/quida-english",
    iconClass: "lesson-book-card-icon-quida",
    icon: "fas fa-graduation-cap",
    titleKey: "lessonBooks.quida.cardTitle",
    descriptionKey: "lessonBooks.quida.cardDescription",
  },
];

export function buildDefaultBooks(t) {
  return DEFAULT_BOOK_ENTRIES.map((e) => ({
    _id: e._id,
    title: t(e.titleKey),
    description: t(e.descriptionKey),
    slug: e.slug,
    path: e.path,
    iconClass: e.iconClass,
    icon: e.icon,
  }));
}

export const VIEW_GRID = "grid";
export const VIEW_LIST = "list";

export function assetUrl(path) {
  if (!path || typeof path !== "string") return "";
  const trimmed = path.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const u = new URL(trimmed);
      if (u.pathname.includes("&")) return "";
      return trimmed;
    } catch {
      return "";
    }
  }
  const base = (s3baseUrl || "").replace(/\/+$/, "");
  const p = trimmed.replace(/^\/+/, "");
  if (!p) return "";
  if (/[&?]/.test(p)) return "";
  if (p.split("/").some((seg) => /[&?]/.test(seg))) return "";
  return `${base}/${p}`;
}

/** True when the URL resolves to a path under /uploads/ (valid stored upload). */
export function hasAssetPreview(imageUrl) {
  if (!imageUrl || typeof imageUrl !== "string") return false;
  const t = imageUrl.trim();
  if (!t) return false;
  try {
    const u = new URL(t);
    return u.pathname.startsWith("/uploads/");
  } catch {
    return false;
  }
}

export function formatLastModified(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  const lng = i18n.language || "en";
  try {
    return new Intl.DateTimeFormat(lng, { month: "short", day: "numeric" }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en", { month: "short", day: "numeric" }).format(d);
  }
}

/** Get file extension from book.file or book.title (lowercase, no dot). */
export function getFileExtension(book) {
  const path = book?.file || book?.title || "";
  if (!path || typeof path !== "string") return "";
  const lower = path.toLowerCase().trim();
  const q = lower.indexOf("?");
  const clean = q >= 0 ? lower.slice(0, q) : lower;
  const lastDot = clean.lastIndexOf(".");
  return lastDot < 0 ? "" : clean.slice(lastDot + 1);
}

export function getFileTypeLabel(book) {
  const tr = (key) => i18n.t(key);
  if (!book?.file) return tr("gallery.fileType.book");
  const raw = (book.file || "").trim();
  if (/^https?:\/\//i.test(raw)) {
    const ext = getFileExtension(book);
    if (!ext) return tr("gallery.fileType.link");
  }
  const lower = raw.toLowerCase();
  if (lower.endsWith(".pdf")) return tr("gallery.fileType.pdf");
  if (lower.endsWith(".csv")) return tr("gallery.fileType.csv");
  if (lower.endsWith(".svg")) return tr("gallery.fileType.svg");
  if ([".jpg", ".jpeg", ".png", ".gif", ".webp"].some((e) => lower.endsWith(e)))
    return tr("gallery.fileType.image");
  if ([".doc", ".docx"].some((e) => lower.endsWith(e))) return tr("gallery.fileType.word");
  if ([".xls", ".xlsx"].some((e) => lower.endsWith(e))) return tr("gallery.fileType.excel");
  if ([".zip", ".rar"].some((e) => lower.endsWith(e))) return tr("gallery.fileType.archive");
  if ([".mp4", ".webm", ".mov"].some((e) => lower.endsWith(e))) return tr("gallery.fileType.video");
  if ([".mp3", ".wav", ".ogg"].some((e) => lower.endsWith(e))) return tr("gallery.fileType.audio");
  return tr("gallery.fileType.document");
}

export function getGalleryFileSubtitle(book) {
  const f = book?.file;
  if (!f || typeof f !== "string") return "";
  const t = f.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      const path = u.pathname || "";
      const shortPath = path.length > 48 ? `${path.slice(0, 45)}…` : path;
      return `${u.hostname}${shortPath === "/" ? "" : shortPath}`;
    } catch {
      return i18n.t("gallery.fileType.externalLink");
    }
  }
  const parts = t.split("/");
  return parts[parts.length - 1] || t;
}

export const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

export function isImageFile(path) {
  if (!path || typeof path !== "string") return false;
  const lower = path.toLowerCase().trim();
  return IMAGE_EXTENSIONS.some((e) => lower.endsWith(e));
}

export function getBookPreviewUrl(book) {
  if (!book) return null;
  if (book.cover_image) return assetUrl(book.cover_image);
  if (book.previewImage) return book.previewImage.startsWith("http") ? book.previewImage : assetUrl(book.previewImage);
  if (book.file && isImageFile(book.file)) return assetUrl(book.file);
  return null;
}

export const formatFileNameToTitle = (filename) => {
  if (!filename) return "";
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
  const spaced = nameWithoutExt.replace(/[-_]/g, " ");
  return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
};
